import { ConfirmationDialog } from 'components/ConfirmationDialog';
import { RequestStatus } from 'features/request/requestSlice';
import { ProjectMethodologyEnum } from 'gql/graphql';
import { CatalogLimitedType } from 'pages/Catalogs/Common/CatalogLimitedType/CatalogLimitedType';
import { CatalogOthersType } from 'pages/Catalogs/Common/CatalogOthersType';
import { useRemoveOtherVariable } from 'pages/Catalogs/Common/CatalogRemoveOtherVariable/hooks/useRemoveOtherVariable';
import { CatalogTablePage } from 'pages/Catalogs/Common/CatalogTablePage';
import { VariableGroups } from 'pages/Catalogs/Common/CatalogVariableGroups';
import { CreateOrEditOtherVariable } from 'pages/Catalogs/Common/CreateOrEditOtherVariable';
import { ViewOtherVariable } from 'pages/Catalogs/Common/CreateOrEditOtherVariable/ViewOtherVariable';
import type { VariableIdType } from 'pages/Catalogs/Common/types';
import { destroyAgGridTableFilters } from 'utils';

import type { AgGridReact } from 'ag-grid-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useCatalog } from '../CatalogList/hooks/useCatalog';

import { CatalogVariablesOtherTable, CatalogVariablesOtherTabs, CatalogVariablesOtherToolbar } from './components';
import { useCatalogVariablesOtherAccess, useCatalogVariablesOtherData } from './hooks';
import type { CatalogVariablesOtherRow } from './types';
import { CatalogVariablesOtherTab } from './types';

export const CatalogVariablesOther = () => {
    const [methodology, setMethodology] = useState(ProjectMethodologyEnum.Uer);
    const [activeTab, setActiveTab] = useState(CatalogVariablesOtherTab.Global);
    const [currentVariable, setCurrentVariable] = useState<VariableIdType>();
    const [currentViewVariableId, setCurrentViewVariableId] = useState<number | undefined>();
    const [isOpenVariableGroups, setIsOpenVariableGroups] = useState(false);
    const [isOpenOtherTypeModal, setIsOpenOtherTypeModal] = useState(false);
    const [isOpenOtherLimitModal, setIsOpenOtherLimitModal] = useState(false);
    const [filterValues, setFilterValues] = useState<Record<string, string[]>>({});

    const { id } = useParams();
    const isGlobal = activeTab === CatalogVariablesOtherTab.Global;
    const gridRef = useRef<AgGridReact<CatalogVariablesOtherRow> | null>(null);

    const { canCreateVariable, canEditDirectory, variablesRules } = useCatalogVariablesOtherAccess(activeTab);
    const { catalogData, fetchingStatus } = useCatalog(id);
    const { datasource, totalCount, fetchedCount } = useCatalogVariablesOtherData({
        catalogId: id,
        globalOnly: isGlobal,
        methodology,
        filterValues,
    });

    const refreshData = useCallback(() => {
        gridRef.current?.api?.purgeInfiniteCache();
    }, []);

    const { isOpenConfirmationDialog, handleDelete, handleOpenRemoveDialog, handleCloseConfirmationDialog } =
        useRemoveOtherVariable({
            updateCatalogVariables: refreshData,
        });

    useEffect(() => {
        setFilterValues({});
        destroyAgGridTableFilters({
            api: gridRef.current?.api,
            columns: ['name', 'groupName', 'subsidiaryName'],
        });
    }, [activeTab, methodology]);

    const handleOpenCreateOrEditDialog = (variableId: VariableIdType) => () => {
        setCurrentVariable(variableId);
    };

    const handleCloseCreateOrEditDialog = () => {
        setCurrentVariable(undefined);
    };

    const handleOpenVariableGroups = () => {
        setIsOpenVariableGroups(true);
    };

    const handleCloseVariableGroups = () => {
        setIsOpenVariableGroups(false);
    };

    const toggleIsOpenOtherTypeModal = () => {
        setIsOpenOtherTypeModal((prev) => !prev);
    };

    const toggleIsOpenOtherLimitModal = () => {
        setIsOpenOtherLimitModal((prev) => !prev);
    };

    const toggleOpenViewVariableDialog = useCallback(
        (variableId: number | undefined) => () => {
            setCurrentViewVariableId(variableId);
        },
        [],
    );

    return (
        <CatalogTablePage
            catalogName={catalogData?.data?.name}
            methodology={catalogData?.data?.type?.methodologies ?? []}
            isLoading={fetchingStatus === RequestStatus.FETCHING && !catalogData?.data?.name}
        >
            <CatalogVariablesOtherToolbar
                methodology={methodology}
                onMethodologyChange={setMethodology}
                handleOpenVariableGroups={handleOpenVariableGroups}
                handleOpenTypeModal={toggleIsOpenOtherTypeModal}
                handleOpenLimitModal={toggleIsOpenOtherLimitModal}
                canEditDirectory={canEditDirectory}
            />

            <CatalogVariablesOtherTabs value={activeTab} onChange={setActiveTab} />

            <CatalogVariablesOtherTable
                key={`${methodology}-${activeTab}`}
                tableRef={gridRef}
                datasource={datasource}
                fetchedCount={fetchedCount}
                totalCount={totalCount}
                onCreate={handleOpenCreateOrEditDialog(null)}
                canCreateVariable={canCreateVariable}
                onEdit={handleOpenCreateOrEditDialog}
                onView={toggleOpenViewVariableDialog}
                onRemove={handleOpenRemoveDialog}
                catalogsRules={variablesRules}
                showSubsidiaryName={!isGlobal}
                catalogUniqueId={id}
                globalOnly={isGlobal}
                methodology={methodology}
                setFilterValues={setFilterValues}
            />

            {typeof currentVariable !== 'undefined' && typeof id !== 'undefined' && (
                <CreateOrEditOtherVariable
                    id={currentVariable}
                    catalogUniqueId={id}
                    onClose={handleCloseCreateOrEditDialog}
                    updateCatalogVariables={refreshData}
                    methodology={methodology}
                    isGlobal={isGlobal}
                />
            )}

            {typeof currentViewVariableId === 'number' && typeof id !== 'undefined' && (
                <ViewOtherVariable
                    id={currentViewVariableId}
                    onClose={toggleOpenViewVariableDialog(undefined)}
                    methodology={methodology}
                    isGlobal={isGlobal}
                />
            )}

            {isOpenConfirmationDialog && (
                <ConfirmationDialog
                    isOpened
                    onSubmit={handleDelete}
                    onClose={handleCloseConfirmationDialog}
                    textHeader="Удаление переменной"
                    textConfirmation="Вы уверены, что хотите удалить переменную?"
                    cancelButtonText="Не удалять"
                    confirmButtonText="Удалить"
                />
            )}

            {isOpenVariableGroups && (
                <VariableGroups
                    onClose={handleCloseVariableGroups}
                    catalogUniqueId={id}
                    methodology={methodology}
                    isGlobal={isGlobal}
                />
            )}

            {isOpenOtherTypeModal && (
                <CatalogOthersType onClose={toggleIsOpenOtherTypeModal} methodology={methodology} />
            )}

            {isOpenOtherLimitModal && (
                <CatalogLimitedType onClose={toggleIsOpenOtherLimitModal} methodology={methodology} />
            )}
        </CatalogTablePage>
    );
};

CatalogVariablesOther.displayName = 'CatalogVariablesOther';
